import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mk, type MutationVars } from "~/state/mutations";
import {
  GUEST,
  generateId,
  useCompletionsData,
  useOwnerId,
  useTasksData,
  writeGuest,
  type Task,
  type TaskCompletion,
} from "~/state/sync";
import type { TaskCadence } from "~/api/types";
import { nextDueOn, rollDueOn, todayIso } from "~/utils/cadence";

export type TaskInput = {
  listId: string;
  title: string;
  notes?: string | null;
  cadence?: TaskCadence;
  weekday?: number | null;
  monthDay?: number | null;
  dueOn?: string;
  sortOrder?: number;
};

function byDueThenTitle(a: Task, b: Task) {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  if (a.due_on !== b.due_on) return a.due_on.localeCompare(b.due_on);
  return a.title.localeCompare(b.title);
}

export function useTasks(listId?: string) {
  const { data } = useTasksData();
  return useMemo(
    () =>
      (data ?? [])
        .filter((task) => !listId || task.list_id === listId)
        .slice()
        .sort(byDueThenTitle),
    [data, listId],
  );
}

export function useTask(taskId: string | undefined) {
  const { data } = useTasksData();
  return useMemo(
    () => (taskId ? ((data ?? []).find((t) => t.id === taskId) ?? null) : null),
    [data, taskId],
  );
}

export function useTaskCount() {
  const { data } = useTasksData();
  return data?.length ?? 0;
}

export function useTaskCompletions(taskId?: string) {
  const { data } = useCompletionsData();
  return useMemo(
    () =>
      (data ?? [])
        .filter((row) => !taskId || row.task_id === taskId)
        .slice()
        .sort((a, b) => a.completed_on.localeCompare(b.completed_on)),
    [data, taskId],
  );
}

export function isCompletedOn(
  task: Task,
  completions: TaskCompletion[],
  day: string,
) {
  if (task.cadence === "once") return Boolean(task.completed_at);
  return completions.some(
    (row) =>
      row.task_id === task.id && row.completed_on === day && !row.deleted,
  );
}

export function isDueOn(
  task: Task,
  completions: TaskCompletion[],
  day: string,
) {
  if (task.deleted) return false;
  if (task.cadence === "once") {
    return !task.completed_at && task.due_on <= day;
  }
  if (isCompletedOn(task, completions, day)) return false;
  return task.due_on <= day;
}

export function useTodayTasks() {
  const { data: taskData } = useTasksData();
  const { data: completionData } = useCompletionsData();
  return useMemo(() => {
    const today = todayIso();
    const tasks = taskData ?? [];
    const completions = completionData ?? [];
    const due = tasks.filter((task) => isDueOn(task, completions, today));
    const overdue = due.filter((task) => task.due_on < today);
    const dueToday = due.filter((task) => task.due_on === today);
    const completedToday = tasks.filter((task) =>
      isCompletedOn(task, completions, today),
    );
    return { today, dueToday, overdue, completedToday, due };
  }, [taskData, completionData]);
}

export function useTaskActions() {
  const userId = useOwnerId();
  const queryClient = useQueryClient();
  const { data: completions } = useCompletionsData();
  const { data: tasks } = useTasksData();
  const scope = userId ?? GUEST;

  const createM = useMutation<unknown, Error, MutationVars["taskCreate"]>({
    mutationKey: [...mk.taskCreate],
  });
  const updateM = useMutation<unknown, Error, MutationVars["taskUpdate"]>({
    mutationKey: [...mk.taskUpdate],
  });
  const removeM = useMutation<unknown, Error, MutationVars["taskRemove"]>({
    mutationKey: [...mk.taskRemove],
  });
  const completeM = useMutation<unknown, Error, MutationVars["taskComplete"]>({
    mutationKey: [...mk.taskComplete],
  });
  const uncompleteM = useMutation<
    unknown,
    Error,
    MutationVars["taskUncomplete"]
  >({ mutationKey: [...mk.taskUncomplete] });

  function taskById(taskId: string) {
    return (tasks ?? []).find((t) => t.id === taskId) ?? null;
  }

  function create(input: TaskInput) {
    const id = generateId();
    const now = new Date().toISOString();
    const cadence = input.cadence ?? "once";
    const row: Task = {
      id,
      owner_id: userId ?? GUEST,
      list_id: input.listId,
      title: input.title.trim(),
      notes: input.notes?.trim() || null,
      cadence,
      weekday:
        cadence === "weekly" ? (input.weekday ?? new Date().getDay()) : null,
      month_day:
        cadence === "monthly" || cadence === "yearly"
          ? (input.monthDay ?? new Date().getDate())
          : null,
      due_on:
        input.dueOn ??
        nextDueOn({
          cadence,
          weekday: input.weekday,
          monthDay: input.monthDay,
        }),
      completed_at: null,
      sort_order: input.sortOrder ?? 0,
      created_at: now,
      updated_at: now,
      deleted: false,
      deleted_at: null,
      last_reminder_sent_on: null,
      line_type: "checklist",
      reminder_time: null,
    };
    if (userId) {
      createM.mutate({ scope, row });
    } else {
      writeGuest<Task>(queryClient, "tasks", (list) => [...list, row]);
    }
    return id;
  }

  function update(taskId: string, input: Partial<TaskInput>) {
    const fields: Partial<Task> = {};
    if (input.listId !== undefined) fields.list_id = input.listId;
    if (input.title !== undefined) fields.title = input.title.trim();
    if (input.notes !== undefined) fields.notes = input.notes?.trim() || null;
    if (input.cadence !== undefined) {
      fields.cadence = input.cadence;
      if (input.cadence !== "weekly") fields.weekday = null;
      if (input.cadence !== "monthly" && input.cadence !== "yearly") {
        fields.month_day = null;
      }
    }
    if (input.weekday !== undefined) fields.weekday = input.weekday;
    if (input.monthDay !== undefined) fields.month_day = input.monthDay;
    if (input.dueOn !== undefined) fields.due_on = input.dueOn;
    if (input.sortOrder !== undefined) fields.sort_order = input.sortOrder;
    if (Object.keys(fields).length === 0) return;
    if (userId) {
      updateM.mutate({ scope, id: taskId, fields });
    } else {
      writeGuest<Task>(queryClient, "tasks", (list) =>
        list.map((t) => (t.id === taskId ? { ...t, ...fields } : t)),
      );
    }
  }

  function complete(taskId: string, completedOn = todayIso()) {
    const task = taskById(taskId);
    if (!task || task.deleted) return;

    const alreadyLogged = (completions ?? []).some(
      (row) => row.task_id === taskId && row.completed_on === completedOn,
    );
    const now = new Date().toISOString();
    const completion: TaskCompletion | undefined = alreadyLogged
      ? undefined
      : {
          id: generateId(),
          task_id: taskId,
          owner_id: userId ?? GUEST,
          completed_on: completedOn,
          created_at: now,
          updated_at: now,
          deleted: false,
        };

    const taskFields: Partial<Task> =
      task.cadence === "once"
        ? { completed_at: now }
        : {
            due_on: rollDueOn({
              cadence: task.cadence,
              weekday: task.weekday,
              monthDay: task.month_day,
              dueOn: task.due_on,
              completedOn,
            }),
          };

    if (userId) {
      completeM.mutate({ scope, completion, taskId, taskFields });
    } else {
      if (completion) {
        writeGuest<TaskCompletion>(queryClient, "task_completions", (list) => [
          ...list,
          completion,
        ]);
      }
      writeGuest<Task>(queryClient, "tasks", (list) =>
        list.map((t) => (t.id === taskId ? { ...t, ...taskFields } : t)),
      );
    }
  }

  function uncomplete(taskId: string, completedOn = todayIso()) {
    const task = taskById(taskId);
    const completionIds = (completions ?? [])
      .filter(
        (row) => row.task_id === taskId && row.completed_on === completedOn,
      )
      .map((row) => row.id);
    const taskFields: Partial<Task> =
      task?.cadence === "once"
        ? { completed_at: null }
        : { due_on: completedOn };

    if (userId) {
      uncompleteM.mutate({ scope, completionIds, taskId, taskFields });
    } else {
      const ids = new Set(completionIds);
      writeGuest<TaskCompletion>(queryClient, "task_completions", (list) =>
        list.filter((row) => !ids.has(row.id)),
      );
      writeGuest<Task>(queryClient, "tasks", (list) =>
        list.map((t) => (t.id === taskId ? { ...t, ...taskFields } : t)),
      );
    }
  }

  function toggleComplete(taskId: string, completedOn = todayIso()) {
    const task = taskById(taskId);
    if (task && isCompletedOn(task, completions ?? [], completedOn)) {
      uncomplete(taskId, completedOn);
    } else {
      complete(taskId, completedOn);
    }
  }

  function remove(taskId: string) {
    if (userId) {
      removeM.mutate({ scope, id: taskId });
    } else {
      writeGuest<Task>(queryClient, "tasks", (list) =>
        list.filter((t) => t.id !== taskId),
      );
    }
  }

  return { create, update, complete, uncomplete, toggleComplete, remove };
}
